import {
  Box,
  Button,
  Divider,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { DashboardTabs } from '../Dashboard/components/DashboardTabs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons';
import CustomerCareSelect from '../Shared/CustomerCareSelect';
import CustomerCareDTable from '../Shared/CustomerCareDTable';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getParentFacilities } from '../../store/facility/actionCreators';
import { RootState } from '../../store';
import FacilityStatusIcon from './components/FacilityStatusIcon';
import NewModal from '../Shared/NewModal';
import AddFacilityController from '../Dashboard/AddNewController/components/AddFacilityController';
import { black, lightBlack } from '../../mainTheme';

interface Options {
  label: string;
  value: string;
}

const FacilitiesOverview = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { parentFacilities, isLoading } = useSelector(
    (state: RootState) => state.facility,
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFacility, setSelectedFacility] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showAddFacility, setShowAddFacility] = useState(false);

  useEffect(() => {
    if (!parentFacilities) {
      dispatch(getParentFacilities());
    }
  }, [dispatch, parentFacilities]);

  const facilityOptions = [
    { label: 'All Facilities', value: 'all' },
    ...(parentFacilities
      ? parentFacilities.map((pf) => ({
          label: pf.parent_facility_name,
          value: pf.parent_facility_id.toString(),
        }))
      : []),
  ];

  // LocationOptions based on selectedFacility
  let locationOptions: Options[] = [{ label: 'All Locations', value: 'all' }];
  if (
    selectedFacility !== 'all' &&
    parentFacilities &&
    parentFacilities.length > 0
  ) {
    const selected = parentFacilities.find(
      (pf) => pf.parent_facility_id.toString() === selectedFacility,
    );
    if (selected && selected.locations && selected.locations.length > 0) {
      locationOptions = [
        { label: 'All Locations', value: 'all' },
        ...selected.locations.map((loc) => ({
          label: loc.name,
          value: loc.id.toString(),
        })),
      ];
    }
  }
  useEffect(() => {
    setSelectedLocation('all');
  }, [selectedFacility]);

  const statusOptions: Options[] = [
    { label: 'All status', value: 'all' },
    { label: '0 issues in the last 24 hrs', value: 'ok' },
    { label: '≤4% issues in the last 24 hrs', value: 'warning' },
    { label: '≥5% issues in the last 24 hrs', value: 'critical' },
  ];

  const filteredData =
    parentFacilities
      ?.filter((pf) => {
        if (
          selectedFacility !== 'all' &&
          pf.parent_facility_id.toString() !== selectedFacility
        )
          return false;
        if (selectedStatus !== 'all' && pf.status !== selectedStatus)
          return false;
        if (selectedLocation !== 'all') {
          const loc = pf.locations.find(
            (l) => l.id.toString() === selectedLocation,
          );
          return !!loc;
        }
        return true;
      })
      .filter((pf) => {
        // Search by facility name or location name
        const facilityMatch = pf.parent_facility_name
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
        const locationMatch =
          pf.locations &&
          pf.locations.some((loc) =>
            loc.name.toLowerCase().includes(searchTerm.toLowerCase()),
          );
        return facilityMatch || locationMatch;
      })
      .map((pf) => ({
        status: pf.status,
        id: pf.parent_facility_id,
        facility_name: pf.parent_facility_name,
        facility_location: pf.total_locations,
        hubs: pf.total_devices ?? 0,
        patient: pf.total_patients ?? 0,
      })) ?? [];

  const columns = [
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.5,
      sortable: true,
      sortComparator: (v1: any, v2: any) => {
        return (v1 ?? '').localeCompare(v2 ?? '');
      },
      renderCell: (params: any) => {
        return (
          <FacilityStatusIcon
            status={params.value as 'ok' | 'warning' | 'critical'}
          />
        );
      },
    },
    { field: 'facility_name', headerName: 'Facility', flex: 2 },
    { field: 'facility_location', headerName: 'Locations', flex: 1 },
    { field: 'hubs', headerName: 'Hubs', flex: 1 },
    {
      field: 'patient',
      headerName: 'Patients',
      flex: 1,
    },
  ];

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleFacilityChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value as string;
    setSelectedFacility(value);
  };

  const handleLocationChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value as string;
    setSelectedLocation(value);
  };

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value as string;
    setSelectedStatus(value);
  };

  const handleResetFilters = () => {
    setSelectedFacility('all');
    setSelectedLocation('all');
    setSelectedStatus('all');
    setSearchTerm('');
  };

  const handleRowClick = (row: any) => {
    // Navigate to the details page for the selected facility
    navigate(`/v2/facilities/${row.id}`);
  };

  return (
    <Box display="flex" flexDirection="column">
      <DashboardTabs
        buttonConfig={{
          show: true,
          label: 'Add new Facility',
          onClick: () => setShowAddFacility(true),
        }}
      />
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        p={2}
      >
        <Typography sx={{ color: lightBlack }}>
          <Box component="span" sx={{ color: black }}>
            {filteredData.length}{' '}
          </Box>
          Facilities
        </Typography>
        <TextField
          placeholder="Search"
          value={searchTerm}
          onChange={handleSearchChange}
          variant="outlined"
          InputProps={{
            startAdornment: (
              <Box display="flex" alignItems="center" mr={1}>
                <FontAwesomeIcon icon={faMagnifyingGlass} size="lg" />
              </Box>
            ),
            sx: {
              borderRadius: '10px',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#D4D4D9',
              },
              color: lightBlack,
              '& input::placeholder': {
                color: '#7F7F7F',
                opacity: 1,
              },
            },
          }}
          sx={{
            minWidth: '566px',
            '& .MuiInputLabel-root': {
              color: lightBlack,
            },
          }}
        />
      </Box>
      <Box px={2} py={1}>
        <Divider />
      </Box>
      <Box display="flex" justifyContent="space-between" p={2}>
        <Box
          display="flex"
          gap={2}
          flexDirection={{ xs: 'column', sm: 'column', md: 'row' }}
        >
          <Box display="flex" flexGrow={1}>
            <CustomerCareSelect
              label="Facility name"
              value={selectedFacility}
              onChange={handleFacilityChange}
              sx={{
                minWidth: '380px',
              }}
            >
              {facilityOptions.map((op: Options) => (
                <MenuItem key={op.value} value={op.value}>
                  {op.label}
                </MenuItem>
              ))}
            </CustomerCareSelect>
          </Box>
          <Box display="flex" flexGrow={1}>
            <CustomerCareSelect
              label="Facility location"
              value={selectedLocation}
              onChange={handleLocationChange}
              sx={{
                minWidth: '200px',
              }}
            >
              {locationOptions.map((op: Options) => (
                <MenuItem key={op.value} value={op.value}>
                  {op.label}
                </MenuItem>
              ))}
            </CustomerCareSelect>
          </Box>

          <Box display="flex">
            <CustomerCareSelect
              label="Status"
              value={selectedStatus}
              onChange={handleStatusChange}
              sx={{
                minWidth: '200px',
              }}
            >
              {statusOptions.map((op: Options) => (
                <MenuItem key={op.value} value={op.value}>
                  {op.value !== 'all' ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <FacilityStatusIcon
                        status={op.value as 'ok' | 'warning' | 'critical'}
                      />
                      <span>{op.label}</span>
                    </Box>
                  ) : (
                    op.label
                  )}
                </MenuItem>
              ))}
            </CustomerCareSelect>
          </Box>
        </Box>
        <Box display="flex">
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleResetFilters}
          >
            Reset
          </Button>
        </Box>
      </Box>
      <Box px={2}>
        <CustomerCareDTable
          rows={filteredData}
          columns={columns}
          loading={isLoading}
          pageSize={10}
          defaultSortField="status"
          defaultSortDirection="asc"
          hideViewAllButton
          onRowClick={handleRowClick}
          minHeight="200px"
        />
      </Box>

      {showAddFacility && (
        <NewModal
          showCloseX
          title="Add new"
          onClose={() => setShowAddFacility(false)}
          maxWidth="xs"
        >
          <AddFacilityController
            onCancel={() => setShowAddFacility(false)}
            onBack={() => setShowAddFacility(false)}
            onSuccess={() => {
              setShowAddFacility(false);
              dispatch(getParentFacilities());
            }}
          />
        </NewModal>
      )}
    </Box>
  );
};

export default FacilitiesOverview;
